import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    Button,
    Collapse,
} from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import { Cancel, ModeEdit } from '@mui/icons-material';
import { BitStringData, WorkHours } from '../../../../types';
import { generateAllOnesBitString, generateScheduleBitString, extractTimesFromBitString, getDateWithSuffix } from '../../../../time/timeUtils';
import { TIME_INTERVAL } from '../../../../constants';
import { Dayjs } from '../../../../utils/dayjs-config';

interface SpecificDayScheduleCardProps {
    date: Dayjs;
    initialWorkHours: BitStringData | null;
    isLoading: Boolean;
    onWorkHoursChange: (date: Dayjs, updatedWorkHours: BitStringData) => void;
    workHours: WorkHours;
    lockedOpen?: boolean;
}

const SpecificDayScheduleCard: React.FC<SpecificDayScheduleCardProps> = ({
    date,
    initialWorkHours,
    isLoading,
    onWorkHoursChange,
    workHours,
    lockedOpen = false,
}) => {
    const [editableWorkHours, setEditableWorkHours] = useState<BitStringData>(extractTimesFromBitString(workHours.monday));
    const [isEditing, setIsEditing] = useState(lockedOpen);

    // Needed to make sure the card has the latest work hours.
    useEffect(() => {
        if (initialWorkHours) {
            if (JSON.stringify(editableWorkHours) !== JSON.stringify(initialWorkHours)) {
                setEditableWorkHours(initialWorkHours);
            }
        }
        else {
            const dayName = date.format('dddd').toLowerCase() as keyof WorkHours;
            const workHoursForDay = workHours[dayName];
            setEditableWorkHours(extractTimesFromBitString(workHoursForDay));
        }
    }, [initialWorkHours, workHours, date]);

    // When we cancel make sure to reset the work hours
    useEffect(() => {
        if (!isEditing) {
            if (initialWorkHours) {
                setEditableWorkHours(initialWorkHours);
            }
            else {
                const dayName = date.format('dddd').toLowerCase() as keyof WorkHours;
                const workHoursForDay = workHours[dayName];
                setEditableWorkHours(extractTimesFromBitString(workHoursForDay));
            }
        }
    }, [date, initialWorkHours, isEditing, workHours]);

    useEffect(() => {
        if (lockedOpen) {
            setIsEditing(true);
        }
    }, [lockedOpen]);

    const handleTimeChange = (type: 'startTime' | 'endTime', newTime: Dayjs) => {
        setEditableWorkHours((prev) => {
            const updatedWorkHours = { ...prev, [type]: newTime };

            if (type === 'startTime' && newTime.isAfter(prev.endTime)) {
                alert('Start time must be before end time.');
                return prev;
            }

            if (type === 'endTime' && newTime.isBefore(prev.startTime)) {
                alert('End time must be after start time.');
                return prev;
            }

            if (!updatedWorkHours.isDayOff) {
                updatedWorkHours.bitString = generateScheduleBitString(updatedWorkHours);
            }

            return updatedWorkHours;
        });
    };

    const handleDayOffChange = (value: boolean) => {
        const updatedWorkHours = {
            ...editableWorkHours,
            isDayOff: value,
            bitString: value ? generateAllOnesBitString() : generateScheduleBitString({ ...editableWorkHours, isDayOff: false }),
        };
        setEditableWorkHours(updatedWorkHours);
    };

    const toggleEdit = () => {
        if (lockedOpen) {
            return;
        }
        setIsEditing(!isEditing);
    };

    const handleSave = async () => {
        console.log("handle save");
        onWorkHoursChange(date, editableWorkHours);
        if (!lockedOpen) {
            toggleEdit();
        }
    }

    return (
        <Box sx={{
            mb: 2,
            p: 1,
            width: '100%',
            opacity: isLoading ? 0.5 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            backgroundColor: isLoading ? 'grey.100' : 'background.paper',
        }}>
            <Box sx={{ px: { xs: 0.5, sm: 1 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {!lockedOpen && (
                        <IconButton onClick={toggleEdit}>
                            {isEditing ? <Cancel /> : <ModeEdit />}
                        </IconButton>
                    )}
                </Box>
                <Collapse in={!isEditing}>
                    <Box display="flex" justifyContent="space-between" flexDirection={'column'} marginTop={2}>
                        {initialWorkHours ? (
                            initialWorkHours.isDayOff ? (
                                <Typography style={{ fontStyle: 'italic', fontWeight: 'bold' }}>Not a work day</Typography>
                            ) : (
                                <>
                                    <Typography>
                                        <span style={{ fontWeight: 'bold' }}>Hours:</span> {initialWorkHours.startTime?.format('h:mm a') || 'N/A'} - {initialWorkHours.endTime?.format('h:mm a') || 'N/A'}
                                    </Typography>
                                </>

                            )) : (
                            <Typography>
                                <span style={{ fontStyle: 'italic', fontWeight: 'bold', color: 'red' }}>No schedule set for this date</span>
                            </Typography>
                        )
                        }
                    </Box>

                </Collapse>

                {/* Main Edit Collapse */}
                <Collapse in={isEditing}>
                    <Box mt={2} display="flex" flexDirection="column" gap={2}>
                        {/* Day Off Checkbox */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={editableWorkHours.isDayOff}
                                    onChange={(e) => handleDayOffChange(e.target.checked)}
                                    disabled={!isEditing}
                                />
                            }
                            label="Take Day Off?"
                        />

                        {/* If it is NOT a day off, show the time pickers and Has Break */}
                        <Collapse in={!editableWorkHours.isDayOff}>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <DesktopTimePicker
                                    label="Start Time"
                                    value={editableWorkHours.startTime}
                                    onChange={(newTime) => newTime && handleTimeChange('startTime', newTime)}
                                    maxTime={editableWorkHours.endTime || undefined}
                                    disabled={!isEditing}
                                    minutesStep={TIME_INTERVAL}
                                />
                                <DesktopTimePicker
                                    label="End Time"
                                    value={editableWorkHours.endTime}
                                    onChange={(newTime) => newTime && handleTimeChange('endTime', newTime)}
                                    minTime={editableWorkHours.startTime || undefined}
                                    disabled={!isEditing}
                                    minutesStep={TIME_INTERVAL}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" mt={2}>
                                Breaks are managed through blockers in the Work Hours view.
                            </Typography>
                        </Collapse>
                    </Box>

                    {isEditing && (
                        <Button variant="contained" color="primary" onClick={handleSave} sx={{ mt: 2 }}>
                            Save
                        </Button>
                    )}
                </Collapse>
            </Box>
        </Box>
    );
};

export default SpecificDayScheduleCard;
